import { TApiTasksFilters } from "@timenotes/shared/src/services/api-client/tasks-api"
import { TApiProject, TApiProjectSection, TApiTaskDetails } from "@timenotes/shared/src/services/api-client/types"
import { Button, Divider, Popover, Tag, Tooltip } from "antd"
import { ColumnType } from "antd/lib/table"
import BillableFlag from "components/base/billable-flag"
import GenericTable, { GenericTableParams } from "components/generic-table/generic-table"
import { TagsColumn } from "components/table-columns/table-columns"
import { useState } from "react"
import { DraggableBodyRow } from "../draggable-sections"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { UniqueIdentifier } from "@dnd-kit/core"
import { useLoadProjectSectionTasks, useProjectBoard } from "@timenotes/shared/src/features/project-board"
import ProjectSectionHeaderContainer from "pages/projects/board/components/project-section-header-container"
import NewTaskFormContainer from "pages/projects/board/components/new-task-form"
import { useTaskDetailsModal } from "components/timenotes/tasks/task-details-modal/task-details-modal"
import { formatTaskDurationWithEstimate } from "@timenotes/shared/src/services/tasks/utils/format-task-duration-with-estimate"
import { isTaskAboveEstimate } from "@timenotes/shared/src/services/tasks/utils/is-task-above-estimate"
import { UserCircle } from "components/timenotes/tasks/task-details-modal/sidebar/task-assignees-select-popover"
import { TaskOptions } from "components/timenotes/tasks/task-details-modal/task-options"
import { ArchiveSvg } from "assets/svg/icon"
import { AddButton } from "pages/projects/board/components/add-button"

interface SectionTasksTableProps {
  project: TApiProject
  projectSection: TApiProjectSection
  filters: TApiTasksFilters
  forceCollapse?: boolean
  //tableProps?: ComponentProps<typeof GenericTable>
}

// TODO: move draggable body row functionality above the Tasks Table
const SectionTasksTable = (props: SectionTasksTableProps) => {
  const {
    projectSection,
    project,
    filters,
    //tableProps: genericTableProps,
  } = props

  // Extracted separatelly to ensure boolean type
  const forceCollapse = !!props.forceCollapse

  const {
    newTaskFormVisible,
    newTaskFormik,
    hideNewTaskForm,
    showNewTaskForm,
  } = useProjectBoard()

  const addTaskOnTopVisible = newTaskFormVisible && newTaskFormik.values.projectSectionId == projectSection.id && newTaskFormik.values.projectSectionPosition == 0
  const addTaskBelowVisible = newTaskFormVisible && newTaskFormik.values.projectSectionId == projectSection.id && newTaskFormik.values.projectSectionPosition != 0

  const {
    tasks,
    meta,
  } = projectSection.tasks

  const loadMoreMutation = useLoadProjectSectionTasks({
    projectId: project.id,
    projectSectionId: projectSection.id,
  })

  const tasksCount = meta.pagination.totalCount
  const canLoadMore = tasksCount > tasks.length

  const [isCollapsed, setIsCollapsed] = useState(!!forceCollapse)
  const displayCollapsed = isCollapsed || forceCollapse

  /// VIEW

  const getTaskIsBillable = (task: TApiTaskDetails) => {
    if (task.isBillable === null) {
      return project.defaultIsBillableTimeLogs
    } else {
      return task.isBillable
    }
  }

  const handleAddTaskAtTheEnd = () => {
    const lastTaskPosition = projectSection.tasks.tasks.length > 0 ? (projectSection.tasks.tasks[projectSection.tasks.tasks.length - 1].projectSectionPosition + 1) : 0

    showNewTaskForm({
      projectSectionId: projectSection.id,
      projectId: projectSection.projectId,
      // New task should have the projectSectionPosition of the last loaded task increased by 1
      projectSectionPosition: lastTaskPosition
    })
  }

  const {
    openTaskModal,
    closeTaskModal,
  } = useTaskDetailsModal()

  const COLUMNS: ColumnType<TApiTaskDetails>[] = [
    {
      title: 'TASK',
      render: (_, task) => {
        return (
          <span
            onClick={() => openTaskModal({
              projectId: projectSection.projectId,
              taskId: task.id,
            })}
          >
            { task.name } { task.state == 'archived' && <Tag><ArchiveSvg /> Archived</Tag>}  
          </span>
        )
      },
      width: '40%',
    },
    {
      title: 'POSITION',
      render: (_, task) => {
        return (
          <span> { task.projectSectionPosition } </span>
        )
      },
      width: '5%',
    },

    {
      title: 'TIME',
      render: (_, task) => (
        <div
          style={{
            width: '180px',
            display: 'flex',
            gap: '15px',
          }}
        >
          <span
            style={{
              color: isTaskAboveEstimate(task) ? 'red' : undefined
            }}
          >
            {formatTaskDurationWithEstimate(task)}
          </span>
        </div>
      ),
    },

    {
      title: "BILLABLE",
      render: (_, task) => (
        <BillableFlag
          checked={getTaskIsBillable(task)}
          clickable={false}
        />
      )
    },

    {
      title: "TAGS",
      render: (_, task) => (
        <div>
          <TagsColumn 
            tags={task.tags} 
            displayPlaceholder={true}
          />
        </div>
      )
    },

    {
      title: "ASSIGNEE",
      render: (_, task) => {

        if (task.assignees.length == 0) {
          return (
            <span>
              <i>
                No assignee
              </i>
            </span>
          )
        }

        const getInitials = (name: string) => {
          const parts = name.split(" ")
          return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name[0].toUpperCase()
        }

        const avatars = task.assignees.map((val) => (
          <Tooltip title={val.name}>
            <UserCircle key={val.id}>
              {getInitials(val.name)}
            </UserCircle>
          </Tooltip>
        ))

        return (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              width: '120px',
              gap: '1px',
            }}
          >
            {avatars}
          </div>
        )
      }
    },

    {
      title: "",
      render: (task) => (
        <div
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <TaskOptions
            taskId={task.id}
            taskState={task.state}
            projectId={task.project.id}
            onDelete={closeTaskModal}
          />
        </div>
      ) // this should be true action buttons list with Details ( open task modal ) / archive / delete actions
    }
  ]

  const tableProps = {
    loading: false,
    style: {
      marginTop: '20px',
    },
    dataSource: tasks,
    columns: COLUMNS,
    //rowKey: (record: TApiTaskDetails) => (record.id),
    rowKey: 'id',
    // TODO: extract draggable functionality above
    onRow: (record: TApiTaskDetails, index: number) => ({
      onClick: () => {
        openTaskModal({
          projectId: projectSection.projectId,
          taskId: record.id,
        })
      }
    }),
    components: {
      body: {
        row: DraggableBodyRow,
      }
    }
  }

  // TODO: Draggable functionality -> extract 
  const draggableTaskKeys = (tasks?.map((task) => task.id) || []) as UniqueIdentifier[]

  const tableParams: GenericTableParams = {
    pagination: null,
    sorting: null,
    selectedAll: false,
    selectedRowKeys: [],
  }

  const handleOnChange = () => {
  }

  const {

  } = useProjectBoard()

  return (
    <>
      <ProjectSectionHeaderContainer
        projectSection={projectSection}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        displayArchived
      />

      { addTaskOnTopVisible && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Popover
            getPopupContainer={(el) => el}
            placement="bottomRight"
            visible={addTaskOnTopVisible}
            content={(
              <NewTaskFormContainer 
                formik={newTaskFormik}
                onHide={hideNewTaskForm}
              />
            )}
          >
          </Popover>
        </div>
      )}

      <div>
        {!forceCollapse && !displayCollapsed && (
          <>
            <SortableContext items={draggableTaskKeys} strategy={verticalListSortingStrategy}>
              <GenericTable<any>
                tableProps={tableProps}
                tableParams={tableParams}
                onChange={handleOnChange}
              />
            </SortableContext>

            { // if more pages avaialble, load more visible
              canLoadMore && (
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => {
                      loadMoreMutation.mutate({})
                    }}
                    loading={loadMoreMutation.isLoading}
                  >
                    Load more
                  </Button>
                </div>
              )
            }

            { // if can not load more - allow to add task below
              !canLoadMore && !addTaskBelowVisible && projectSection.tasks.tasks.length > 0 && (
                <AddButton
                  onClick={handleAddTaskAtTheEnd}
                >
                  Add task
                </AddButton>
              )
            }

            { 
              !canLoadMore && addTaskBelowVisible && (
                <div
                  style={{
                    width: '300px'
                  }}
                >
                  <NewTaskFormContainer 
                    formik={newTaskFormik}
                    onHide={hideNewTaskForm}
                  />
                </div>
              )
            }

          </>
        )}

      </div>

      <Divider />
    </>
  )
}

export default SectionTasksTable